// FaceLock Scan picture checks on grey pixels: sharpness (Laplacian
// variance), exposure (too dark, too bright, backlit), a shadow across the
// face, covered eyes (sunglasses, hair) and a shaded forehead (hat, hood),
// plus the tiny thumbnail the near-duplicate check compares. Pure: the DOM
// side (capture.ts, or the pixel worker) only hands over pixels, and only a
// small crop around the face or the person (never the whole frame).

import type { Box } from "./geometry";

/** Grey pixels, 0..1. */
export interface Gray {
  data: Float32Array;
  w: number;
  h: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Rec. 601 luma of RGBA bytes. */
export function toGray(rgba: ArrayLike<number>, w: number, h: number): Gray {
  const data = new Float32Array(w * h);
  for (let i = 0, p = 0; i < data.length; i++, p += 4) {
    data[i] = (0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2]) / 255;
  }
  return { data, w, h };
}

export function clipRect(r: Rect, g: { w: number; h: number }): Rect | null {
  const x = Math.max(0, Math.floor(r.x));
  const y = Math.max(0, Math.floor(r.y));
  const x2 = Math.min(g.w, Math.ceil(r.x + r.w));
  const y2 = Math.min(g.h, Math.ceil(r.y + r.h));
  return x2 - x >= 1 && y2 - y >= 1 ? { x, y, w: x2 - x, h: y2 - y } : null;
}

export const rectOfBox = (b: Box): Rect => ({ x: b.x1, y: b.y1, w: b.x2 - b.x1, h: b.y2 - b.y1 });

/**
 * Variance of the 4-neighbour Laplacian, on the 0..255 scale. Motion blur and
 * a missed focus flatten edges and drive it down. Compare only pictures of the
 * same framing and size (capture.ts resamples every region to 256 px first).
 */
export function laplacianVariance(g: Gray, region?: Rect): number {
  const r = region ? clipRect(region, g) : { x: 0, y: 0, w: g.w, h: g.h };
  if (!r || r.w < 3 || r.h < 3) return 0;
  let n = 0;
  let sum = 0;
  let sum2 = 0;
  for (let y = r.y + 1; y < r.y + r.h - 1; y++) {
    const row = y * g.w;
    for (let x = r.x + 1; x < r.x + r.w - 1; x++) {
      const i = row + x;
      const v = 255 * (g.data[i - 1] + g.data[i + 1] + g.data[i - g.w] + g.data[i + g.w] - 4 * g.data[i]);
      sum += v;
      sum2 += v * v;
      n++;
    }
  }
  if (!n) return 0;
  const mean = sum / n;
  return Math.max(0, sum2 / n - mean * mean);
}

export interface RegionStats {
  mean: number;
  std: number;
  /** Share of blown-out pixels (> 0.97). */
  over: number;
  n: number;
}

export function regionStats(g: Gray, region?: Rect): RegionStats {
  const r = region ? clipRect(region, g) : { x: 0, y: 0, w: g.w, h: g.h };
  if (!r) return { mean: 0, std: 0, over: 0, n: 0 };
  let n = 0;
  let sum = 0;
  let sum2 = 0;
  let over = 0;
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      const v = g.data[y * g.w + x];
      sum += v;
      sum2 += v * v;
      if (v > 0.97) over++;
      n++;
    }
  }
  const mean = n ? sum / n : 0;
  return { mean, std: n ? Math.sqrt(Math.max(0, sum2 / n - mean * mean)) : 0, over: n ? over / n : 0, n };
}

/** An n x n area-averaged thumbnail, 0..1: what the near-duplicate check compares. */
export function thumbnail(g: Gray, n = 8, region?: Rect): number[] {
  const r = (region && clipRect(region, g)) || { x: 0, y: 0, w: g.w, h: g.h };
  const out: number[] = [];
  for (let ty = 0; ty < n; ty++) {
    for (let tx = 0; tx < n; tx++) {
      const x0 = r.x + Math.floor((tx * r.w) / n);
      const x1 = r.x + Math.max(Math.floor(((tx + 1) * r.w) / n), Math.floor((tx * r.w) / n) + 1);
      const y0 = r.y + Math.floor((ty * r.h) / n);
      const y1 = r.y + Math.max(Math.floor(((ty + 1) * r.h) / n), Math.floor((ty * r.h) / n) + 1);
      let s = 0;
      let c = 0;
      for (let y = y0; y < Math.min(y1, g.h); y++) for (let x = x0; x < Math.min(x1, g.w); x++) { s += g.data[y * g.w + x]; c++; }
      out.push(c ? Number((s / c).toFixed(4)) : 0);
    }
  }
  return out;
}

// ---------------------------------------------------------- exposure --

/**
 * The exposure of a region, from a 64-bin histogram: the mean, the 10th and
 * 90th percentiles, and the clipped shares at each end. `ellipse` counts only
 * the pixels inside the ellipse inscribed in the region (a face box's corners
 * are background and hair).
 */
export interface Exposure {
  mean: number;
  p10: number;
  p90: number;
  /** Share at or above LIGHT.clipHigh (blown out). */
  clipHi: number;
  /** Share at or below LIGHT.clipLow (crushed to black). */
  clipLo: number;
  n: number;
}

const BINS = 64;

export function exposureOf(g: Gray, region?: Rect, ellipse = false): Exposure {
  const r = region ? clipRect(region, g) : { x: 0, y: 0, w: g.w, h: g.h };
  const empty: Exposure = { mean: 0, p10: 0, p90: 0, clipHi: 0, clipLo: 0, n: 0 };
  if (!r) return empty;
  const hist = new Uint32Array(BINS);
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const rx = r.w / 2;
  const ry = r.h / 2;
  let n = 0;
  let sum = 0;
  let hi = 0;
  let lo = 0;
  for (let y = r.y; y < r.y + r.h; y++) {
    const dy = (y + 0.5 - cy) / ry;
    for (let x = r.x; x < r.x + r.w; x++) {
      if (ellipse) {
        const dx = (x + 0.5 - cx) / rx;
        if (dx * dx + dy * dy > 1) continue;
      }
      const v = g.data[y * g.w + x];
      sum += v;
      if (v >= LIGHT.clipHigh) hi++;
      if (v <= LIGHT.clipLow) lo++;
      hist[Math.min(BINS - 1, Math.max(0, Math.floor(v * BINS)))]++;
      n++;
    }
  }
  if (!n) return empty;
  const pct = (q: number): number => {
    const want = q * n;
    let acc = 0;
    for (let i = 0; i < BINS; i++) {
      acc += hist[i];
      if (acc >= want) return (i + 0.5) / BINS;
    }
    return 1;
  };
  return { mean: sum / n, p10: pct(0.1), p90: pct(0.9), clipHi: hi / n, clipLo: lo / n, n };
}

// ------------------------------------------------------------ the light --

/**
 * Every light and cover rule, in one place (8-bit values written as x / 255).
 *
 * Tuned for what webcams and phone front cameras actually deliver: auto
 * exposure pulls the face toward mid-grey, a lit face sits around 90-190,
 * forehead and nose highlights clip a few per cent of it, and a dark-skinned
 * face in ordinary light sits around 55-100 with its highlights well above
 * that. So a rule only fires on REAL over- or under-exposure, and needs two
 * kinds of evidence:
 *
 * - too bright: the face mean above 215 AND more than 15 % of the face at 250
 *   or more (a bright face that is not clipped keeps its detail);
 * - too dark: the face mean under 40 AND its highlights (90th percentile)
 *   under 76 (dark skin in ordinary light keeps bright highlights);
 * - backlight: the face underexposed the same way (a little wider) AND the
 *   ring around the head more than twice as bright. A bright wall behind a
 *   well-exposed face is never "backlight": the rule is about the FACE.
 *
 * Every rule has a way out (hysteresis): once raised it clears only past a
 * margin, and it is raised only after LIGHT.enterFrames frames in a row, so a
 * message never flickers.
 */
export const LIGHT = {
  clipHigh: 250 / 255,
  clipLow: 10 / 255,
  bright: { mean: 215 / 255, clipped: 0.15 },
  dark: { mean: 40 / 255, p90: 76 / 255 },
  /** Backlight: the dark rule widened by these, with the ring around the head at least `surround` and twice the face. */
  backlight: { widenMean: 12 / 255, widenP90: 16 / 255, surround: 0.55, ratio: 0.5 },
  /** Hysteresis: how far past its threshold a raised problem must get before it clears. */
  exit: { mean: 10 / 255, clipped: 0.05, p90: 12 / 255, ratio: 0.07 },
  /** One cheek this many times brighter than the other (and at least `minDiff` brighter): a hard side shadow. */
  sideShadow: { ratio: 1.9, minDiff: 0.12 },
  /** Eyes this dark next to the cheeks, and dark in themselves: sunglasses, hair or a brim over them. */
  eyesCovered: { ratio: 0.35, maxEyes: 0.2 },
  /** Forehead this dark next to the cheeks, and dark in itself: a hat or hood shading it. */
  foreheadShaded: { ratio: 0.5, maxForehead: 0.3 },
  /** Frames in a row before a problem is raised. */
  enterFrames: 2,
} as const;

/**
 * What the anti-loop guard gives when a part is stuck (engine.ts): level 1
 * after 8 s, level 2 after 16 s. From level 2 the shading rules only advise
 * (they no longer block), and only extreme exposure still blocks.
 */
export const LIGHT_RELAX = [
  { brightMean: 215 / 255, brightClipped: 0.15, darkMean: 40 / 255, darkP90: 76 / 255, side: 1.9, eyes: 0.35, forehead: 0.5 },
  { brightMean: 225 / 255, brightClipped: 0.22, darkMean: 32 / 255, darkP90: 64 / 255, side: 2.4, eyes: 0.28, forehead: 0.4 },
  { brightMean: 235 / 255, brightClipped: 0.3, darkMean: 26 / 255, darkP90: 56 / 255, side: Infinity, eyes: 0, forehead: 0 },
] as const;

export type LightProblem = "dark" | "bright" | "backlight" | "sideShadow" | "eyesCovered" | "foreheadShaded";

/** Worst first. */
export const LIGHT_ORDER: readonly LightProblem[] = ["dark", "backlight", "bright", "sideShadow", "eyesCovered", "foreheadShaded"];

/** What the light analysis measured on one face (or person). Values 0..1. */
export interface LightStats {
  /** The face (inside the ellipse of its box) or the person. */
  face: Exposure;
  /** The ring around the head (or the person) in the analysed crop; null when too little of it was in the picture. */
  surround: number | null;
  rightCheek: number | null;
  leftCheek: number | null;
  eyes: number | null;
  forehead: number | null;
}

const relaxOf = (level: number) => LIGHT_RELAX[Math.max(0, Math.min(LIGHT_RELAX.length - 1, Math.floor(level)))];

/** Does problem `p` hold on `s`? `stay` = it is already raised (the exit margins apply). */
function holds(p: LightProblem, s: LightStats, frontal: boolean, level: number, stay: boolean): boolean {
  const L = relaxOf(level);
  const f = s.face;
  if (!f.n) return false;
  const m = stay ? LIGHT.exit.mean : 0;
  const q = stay ? LIGHT.exit.p90 : 0;
  const k = stay ? LIGHT.exit.ratio : 0;
  switch (p) {
    case "bright":
      return f.mean > L.brightMean - m && f.clipHi >= L.brightClipped - (stay ? LIGHT.exit.clipped : 0);
    case "dark":
      return f.mean < L.darkMean + m && f.p90 < L.darkP90 + q;
    case "backlight": {
      if (s.surround === null) return false;
      const bl = LIGHT.backlight;
      return s.surround >= bl.surround - (stay ? 0.05 : 0)
        && f.mean < L.darkMean + bl.widenMean + m
        && f.p90 < L.darkP90 + bl.widenP90 + q
        && f.mean < s.surround * (bl.ratio + k);
    }
    default: {
      if (!frontal || s.rightCheek === null || s.leftCheek === null) return false;
      const hi = Math.max(s.rightCheek, s.leftCheek);
      const lo = Math.max(0.01, Math.min(s.rightCheek, s.leftCheek));
      const cheek = (s.rightCheek + s.leftCheek) / 2;
      if (p === "sideShadow") return hi / lo > L.side - (stay ? 0.2 : 0) && hi - lo >= LIGHT.sideShadow.minDiff - (stay ? 0.02 : 0);
      if (cheek <= 0.05) return false;
      if (p === "eyesCovered") return s.eyes !== null && s.eyes / cheek < L.eyes + k && s.eyes < LIGHT.eyesCovered.maxEyes + (stay ? 0.04 : 0);
      return s.forehead !== null && s.forehead / cheek < L.forehead + k && s.forehead < LIGHT.foreheadShaded.maxForehead + (stay ? 0.04 : 0);
    }
  }
}

/** Backlight names the cause of a dark face: when it holds, "dark" is not said too. */
function tidy(list: LightProblem[]): LightProblem[] {
  return list.includes("backlight") ? list.filter((p) => p !== "dark") : list;
}

/**
 * What is wrong with the light, worst first, judged on one frame (a photo).
 * `frontal` limits the cheek, eye and forehead comparisons to a face seen
 * from the front (a turned face shades its far side on its own).
 */
export function lightProblems(s: LightStats | null, frontal: boolean, level = 0): LightProblem[] {
  if (!s) return [];
  return tidy(LIGHT_ORDER.filter((p) => holds(p, s, frontal, level, false)));
}

/**
 * The live version: a problem is raised after LIGHT.enterFrames frames in a
 * row and cleared only past its exit margin, so the words on screen do not
 * flicker between "too dark" and nothing on a borderline face.
 */
export class LightGate {
  private active = new Set<LightProblem>();
  private streak = new Map<LightProblem, number>();

  update(s: LightStats | null, frontal: boolean, level = 0): LightProblem[] {
    if (!s || !s.face.n) {
      this.reset();
      return [];
    }
    for (const p of LIGHT_ORDER) {
      if (this.active.has(p)) {
        if (!holds(p, s, frontal, level, true)) {
          this.active.delete(p);
          this.streak.delete(p);
        }
        continue;
      }
      if (holds(p, s, frontal, level, false)) {
        const n = (this.streak.get(p) ?? 0) + 1;
        this.streak.set(p, n);
        if (n >= LIGHT.enterFrames) this.active.add(p);
      } else {
        this.streak.delete(p);
      }
    }
    return tidy(LIGHT_ORDER.filter((p) => this.active.has(p)));
  }

  reset(): void {
    this.active.clear();
    this.streak.clear();
  }
}

/** Exposure only (no face model): the light check's rule, same thresholds. */
export function exposureProblems(e: Exposure): Array<"dark" | "bright"> {
  const s: LightStats = { face: e, surround: null, rightCheek: null, leftCheek: null, eyes: null, forehead: null };
  return lightProblems(s, false).filter((p): p is "dark" | "bright" => p === "dark" || p === "bright");
}

// ------------------------------------------------------ the analysis --

/** A point or a rectangle as a share of the analysed crop (0..1). */
export interface NPoint { x: number; y: number }
export interface NRect { x: number; y: number; w: number; h: number }

/**
 * One small crop to analyse. `crop` is read from the camera frame (px) at
 * outW x outH (the subject's long side about ANALYSIS.subjectPx); `subject`
 * and `points` are shares of that crop, so any resampling keeps them right.
 */
export interface PixelRequest {
  kind: "face" | "body";
  crop: { x1: number; y1: number; x2: number; y2: number };
  outW: number;
  outH: number;
  subject: NRect;
  points?: {
    rightCheek: NPoint | null;
    leftCheek: NPoint | null;
    rightEye: NPoint | null;
    leftEye: NPoint | null;
    forehead: NPoint | null;
  };
}

export interface PixelReport {
  light: LightStats;
  /** Laplacian variance of the subject (the inner 80 % of its box), 0..255 scale. */
  sharpness: number;
  /** 8 x 8 thumbnail of the subject (the near-duplicate check). */
  thumb: number[];
}

/** How big the analysed crop is: the face (or person) about this many px on its long side, plus a ring around it. */
export const ANALYSIS = { subjectPx: 160, faceRing: 0.3, bodyRing: 0.15, cheekR: 0.07, eyeR: 0.045, foreheadR: 0.07 } as const;

/** Everything the scan measures on pixels, from one small grey crop. Pure (the worker runs this too). */
export function analyzeGray(g: Gray, req: PixelRequest): PixelReport {
  const sub: Rect = { x: req.subject.x * g.w, y: req.subject.y * g.h, w: req.subject.w * g.w, h: req.subject.h * g.h };
  const face = exposureOf(g, sub, req.kind === "face");
  // The ring: every pixel of the crop outside the subject box.
  const all = regionStats(g);
  const box = regionStats(g, sub);
  const ringN = all.n - box.n;
  const surround = ringN > all.n * 0.05 && ringN > 0 ? (all.mean * all.n - box.mean * box.n) / ringN : null;
  const long = Math.max(sub.w, sub.h);
  const at = (p: NPoint | null | undefined, rFrac: number): number | null => {
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    const r = Math.max(1, long * rFrac);
    const s = regionStats(g, { x: p.x * g.w - r, y: p.y * g.h - r, w: 2 * r, h: 2 * r });
    return s.n ? s.mean : null;
  };
  const pts = req.points;
  const re = at(pts?.rightEye, ANALYSIS.eyeR);
  const le = at(pts?.leftEye, ANALYSIS.eyeR);
  const inner: Rect = { x: sub.x + sub.w * 0.1, y: sub.y + sub.h * 0.1, w: sub.w * 0.8, h: sub.h * 0.8 };
  return {
    light: {
      face,
      surround,
      rightCheek: at(pts?.rightCheek, ANALYSIS.cheekR),
      leftCheek: at(pts?.leftCheek, ANALYSIS.cheekR),
      eyes: re !== null && le !== null ? (re + le) / 2 : null,
      forehead: at(pts?.forehead, ANALYSIS.foreheadR),
    },
    sharpness: laplacianVariance(g, inner),
    thumb: thumbnail(g, 8, sub),
  };
}

// ------------------------------------------------------------- sharpness --

export const SHARPNESS = {
  /** Never accept below this, whatever the camera (the subject at ANALYSIS.subjectPx, 0..255 scale). */
  floor: 25,
  /** At least this share of what this camera reaches on a still frame. */
  relative: 0.6,
  /** A frame kept from the turn buffer (the peak of a turn): a little more lenient. */
  relativeTurn: 0.5,
  /** Samples before the relative rule applies. */
  warmup: 8,
  keep: 40,
} as const;

/**
 * "Sharp" for THIS camera: the absolute floor, and at least 60 % of the 80th
 * percentile of recent still frames of the same kind. Motion blur from a
 * turn falls well under it; a soft but still webcam does not.
 */
export class SharpnessTracker {
  private values: number[] = [];

  push(v: number): void {
    if (!Number.isFinite(v)) return;
    this.values.push(v);
    if (this.values.length > SHARPNESS.keep) this.values.shift();
  }

  reference(): number | null {
    if (this.values.length < SHARPNESS.warmup) return null;
    const s = [...this.values].sort((a, b) => a - b);
    return s[Math.min(s.length - 1, Math.floor(s.length * 0.8))];
  }

  isSharp(v: number, relative: number = SHARPNESS.relative): boolean {
    if (!(v >= SHARPNESS.floor)) return false;
    const ref = this.reference();
    return ref === null || v >= ref * relative;
  }

  reset(): void {
    this.values = [];
  }
}
